import LegalLayout, { H2, H3, UL } from "@/components/layout/LegalLayout";

export default function Sla() {
  return (
    <LegalLayout titleKey="sla" title="Service Level Agreement (SLA)" testId="legal-sla-page">
      <H2>Overview</H2>
      <p>
        This Service Level Agreement (&quot;SLA&quot;) describes the availability commitments
        and operational standards AtlasForge Financial provides for its
        platform, including Safe to Spend 365, Ember360, and the APIs for Banks
        &amp; Fintechs.
      </p>
      <p>
        This SLA applies to customers on Growth and Enterprise plans. Starter
        and free-tier accounts receive best-effort support without committed
        uptime targets.
      </p>

      <H2>Availability Commitment</H2>
      <p>
        AtlasForge Financial targets the following monthly uptime by tier:
      </p>
      <UL items={[
        "Starter — best-effort (no committed SLA)",
        "Growth — 99.9% monthly uptime",
        "Enterprise — 99.95% monthly uptime, with dedicated incident response",
      ]} />
      <p>
        &quot;Monthly Uptime&quot; is calculated as: total minutes in the month minus
        minutes of Unavailability, divided by total minutes in the month.
      </p>

      <H3>What counts as Unavailability</H3>
      <UL items={[
        "Sustained 5xx error rates above 1% on production API endpoints",
        "Full outage of the Partner Portal",
        "Inability to ingest events through the Ingest API for more than 15 consecutive minutes",
      ]} />

      <H3>What does NOT count as Unavailability</H3>
      <UL items={[
        "Scheduled maintenance announced at least 48 hours in advance",
        "Degradation caused by partner integrations or third-party providers (e.g. Plaid, Stripe, banking partners)",
        "Issues caused by customer code, misconfiguration, or invalid credentials",
        "Force majeure events outside of our reasonable control",
      ]} />

      <H2>Service Credits</H2>
      <p>If we fail to meet the SLA in a given calendar month:</p>
      <UL items={[
        "99.0% – 99.9% uptime → 10% service credit",
        "95.0% – 99.0% uptime → 25% service credit",
        "Below 95.0% uptime → 50% service credit",
      ]} />
      <p>
        Credits are applied to the next billing cycle and are the customer&apos;s
        sole and exclusive remedy for SLA breaches. Credit requests must be
        submitted within 30 days of the affected period.
      </p>

      <H2>Incident Response</H2>
      <p>Severity levels and target response times:</p>
      <UL items={[
        "Sev 1 (full outage) — initial response within 15 minutes, 24×7",
        "Sev 2 (major degradation) — initial response within 1 hour, 24×7",
        "Sev 3 (minor impact) — initial response within 1 business day",
        "Sev 4 (informational) — initial response within 2 business days",
      ]} />

      <H2>Status &amp; Communication</H2>
      <p>
        Real-time platform status is published at{" "}
        <a href="/status" className="underline">atlasforgefinancial.com/status</a>.
        Incident updates are posted there and emailed to designated technical
        contacts on Enterprise plans.
      </p>

      <H2>Scheduled Maintenance</H2>
      <p>
        Whenever possible, scheduled maintenance is performed during the
        following window:
      </p>
      <UL items={[
        "Saturdays 02:00 – 06:00 US Eastern Time",
        "Announced at least 48 hours in advance via the Status page",
      ]} />

      <H2>Customer Responsibilities</H2>
      <p>To maintain SLA eligibility, customers agree to:</p>
      <UL items={[
        "Keep API keys current and secure",
        "Implement exponential backoff and retry logic on requests",
        "Respect documented rate limits",
        "Report suspected issues promptly to customersupport@atlasforgefinancial.com",
      ]} />

      <H2>Changes to this SLA</H2>
      <p>
        AtlasForge may update this SLA from time to time. Material changes
        will be communicated to active customers at least 30 days before
        taking effect.
      </p>

      <H2>Contact</H2>
      <p>For SLA questions or to submit a credit request:</p>
      <p>customersupport@atlasforgefinancial.com</p>
      <p>843-291-2556</p>
    </LegalLayout>
  );
}
